import { Component } from '@theme/component';

/**
 * Loads inline clips only in view and opens a focused fullscreen viewer on tap.
 * A clip starts downloading a little before it scrolls into view (NEAR_MARGIN), so it is ready to play when it
 * appears instead of starting its download only then; it plays only while actually on screen.
 * @extends {Component<{videos: HTMLVideoElement[], viewer: HTMLDialogElement, fullVideo: HTMLVideoElement, closeButton: HTMLButtonElement}>}
 */
/** How far ahead of the screen a clip starts downloading. */
const NEAR_MARGIN = '600px 0px';

class SlProductClips extends Component {
  #observer;
  #nearObserver;
  #controller;
  #visible = new Set();
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #opener;
  #fullscreen = false;
  #overflow;
  #openedAt = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #setup() {
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.#motion.addEventListener('change', this.#sync, options);
    document.addEventListener('visibilitychange', this.#sync, options);
    document.addEventListener('sl:media-retry', () => setTimeout(this.#sync, 300), options);
    // Stillwell: the viewer closes only when fullscreen is left altogether (a fullscreen change to the video inside it,
    // e.g. a double click on the video, closed it)
    document.addEventListener('fullscreenchange', () => {
      if (document.fullscreenElement === this.refs.viewer) this.#fullscreen = true;
      else if (this.#fullscreen && !document.fullscreenElement) this.closeVideo();
    }, options);
    // iPhone: the clip plays in the native player (webkitEnterFullscreen); its Done button ends here, so the dialog does
    // not stay open behind with the page locked
    this.refs.fullVideo?.addEventListener('webkitendfullscreen', () => {
      if (this.refs.viewer?.open) this.closeVideo();
    }, options);
    // Esc, the Android back gesture and any other native close end up here too (the on:close attribute never fired:
    // the component library does not delegate 'close'), so scrolling comes back and the HD video stops
    this.refs.viewer?.addEventListener('close', () => this.closeVideo(), options);
    // Stillwell: the viewer opens muted and the shopper may turn the sound on (a listener used to mute it again at
    // once, so the native speaker button looked broken)
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#visible.add(entry.target);
          else this.#visible.delete(entry.target);
        }
        this.#sync();
      }, { threshold: 0.01 });
      for (const video of this.refs.videos ?? []) this.#observer.observe(video);
      this.#nearObserver = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#poster(/** @type {HTMLVideoElement} */ (entry.target));
          this.#load(/** @type {HTMLVideoElement} */ (entry.target));
          this.#nearObserver?.unobserve(entry.target);
        }
      }, { rootMargin: NEAR_MARGIN });
      for (const video of this.refs.videos ?? []) this.#nearObserver.observe(video);
    } else {
      for (const video of this.refs.videos ?? []) this.#poster(video);
    }
  }

  /**
   * Sets the poster (data-poster) once the clip is near the screen; it used to download with the page.
   * @param {HTMLVideoElement} video
   */
  #poster(video) {
    if (video.dataset.poster && !video.getAttribute('poster')) video.poster = video.dataset.poster;
  }

  /**
   * The fullscreen file: HLS where the browser plays it natively (it adapts to the connection), else the 720p or
   * 480p file on phones and slow or data-saving connections, the sharpest one on fast desktops.
   * @param {HTMLVideoElement} inline
   * @param {HTMLVideoElement} full
   */
  #pickFull(inline, full) {
    const d = inline.dataset;
    if (d.hls && full.canPlayType('application/vnd.apple.mpegurl')) return d.hls;
    const c = /** @type {any} */ (navigator).connection;
    const down = c && typeof c.downlink === 'number' && c.downlink > 0 ? c.downlink : 0;
    if ((c && c.saveData) || (down && down < 2)) return d.low || d.mid || d.full || d.src;
    if ((down && down < 5) || window.matchMedia('(max-width: 899px)').matches) return d.mid || d.full || d.src;
    return d.full || d.mid || d.src;
  }

  /**
   * Gives the clip its source and lets the browser buffer it, unless motion is reduced (posters only then).
   * @param {HTMLVideoElement} video
   */
  #load(video) {
    if (this.#motion.matches || this.#slow() || video.hasAttribute('src') || !video.dataset.src) return;
    video.preload = 'auto';
    video.src = video.dataset.src;
  }

  /**
   * Stillwell: data saver or a connection under 2 Mbps. The three previews (240-740KB each) played together and took the
   * whole bandwidth of a slow phone; there they stay posters and a tap opens the clip in the viewer.
   */
  #slow() {
    const c = /** @type {any} */ (navigator).connection;
    return !!c && (c.saveData === true || /(^|-)2g$/.test(c.effectiveType || '') || (typeof c.downlink === 'number' && c.downlink > 0 && c.downlink < 2));
  }

  #sync = () => {
    const slow = this.#slow();
    for (const video of this.refs.videos ?? []) {
      if (!this.#visible.has(video) || document.hidden || this.refs.viewer?.open || this.#motion.matches || (slow && !video.hasAttribute('src'))) {
        video.pause();
        continue;
      }
      this.#load(video);
      video.muted = true;
      if (video.paused) video.play().catch(() => { /* The poster and fullscreen button remain available. */ });
    }
  };

  /** @param {MouseEvent} event - Pointer or keyboard activation of the native button. */
  openVideo(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('button');
    const inline = button?.closest('.sl-pdp__clip')?.querySelector('video');
    const { viewer, fullVideo, closeButton } = this.refs;
    if (!(inline instanceof HTMLVideoElement) || !viewer || !fullVideo || !inline.dataset.src) return;
    this.#opener = button;
    // Fullscreen gets a file the connection can carry (see #pickFull); the inline clip keeps its small one
    fullVideo.src = this.#pickFull(inline, fullVideo) || inline.dataset.src;
    fullVideo.poster = inline.poster || inline.dataset.poster || '';
    fullVideo.setAttribute('aria-label', inline.getAttribute('aria-label') ?? '');
    fullVideo.muted = true;
    this.#openedAt = Date.now();
    viewer.showModal();
    closeButton?.focus({ preventScroll: true });
    // Stillwell: the page behind is locked after the viewer is on screen: switching the body's overflow in the same
    // click re-laid out the whole 21,000px page before the viewer could paint (tap to open took 184ms on a mid phone)
    requestAnimationFrame(() => setTimeout(() => {
      if (!viewer.open) return;
      // Only the page's own value is kept (a second open must not store the 'hidden' set by the first)
      if (this.#overflow === undefined) this.#overflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }, 0));
    this.#sync();
    fullVideo.play().catch(() => { /* Native controls allow retry after a loading failure. */ });
    // Request within the activation event; do not await loading or playback first.
    if (viewer.requestFullscreen) viewer.requestFullscreen().catch(() => { /* Keep the viewport-sized dialog. */ });
    else if (typeof fullVideo.webkitEnterFullscreen === 'function') {
      try { fullVideo.webkitEnterFullscreen(); } catch { /* Keep the viewport-sized dialog. */ }
    }
  }

  /** Close, restore focus and resume only clips that are still visible. */
  closeVideo() {
    const { viewer, fullVideo } = this.refs;
    this.#fullscreen = false;
    if (document.fullscreenElement === viewer) document.exitFullscreen().catch(() => {});
    fullVideo?.pause();
    if (fullVideo?.hasAttribute('src')) {
      fullVideo.removeAttribute('src');
      fullVideo.load();
    }
    if (viewer?.open) viewer.close();
    if (this.#overflow !== undefined) document.body.style.overflow = this.#overflow;
    this.#overflow = undefined;
    this.#opener?.focus({ preventScroll: true });
    this.#opener = undefined;
    this.#sync();
  }

  /**
   * Close only a click on the dialog's own surface. The component library hands handlers an event whose target is the
   * element carrying on:click (the dialog itself), so the real target comes from composedPath(): every click on the
   * video (to pause it or turn the sound on) closed the viewer, and a double click on a clip opened and closed it.
   * Clicks in the first 400ms after opening are the rest of a double click and are ignored.
   * @param {MouseEvent} event
   */
  handleBackdrop(event) {
    const origin = event.composedPath?.()[0];
    if (origin !== this.refs.viewer || Date.now() - this.#openedAt < 400) return;
    this.closeVideo();
  }

  #cleanup() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#nearObserver?.disconnect();
    this.#visible.clear();
    this.closeVideo();
    for (const video of this.refs.videos ?? []) video.pause();
  }
}

if (!customElements.get('sl-product-clips')) customElements.define('sl-product-clips', SlProductClips);
